import {
    FormControl,
    FormControlProps,
    FormHelperText,
    FormLabel,
    InputLabel,
    MenuItem,
    Select as MUISelect,
    SelectProps,
    SxProps,
    Typography,
} from '@mui/material';
import { useField } from 'formik';
import { ReactNode } from 'react';

export interface Option {
    value: string | number | boolean;
    label: string;
    /** Quieter text at the row's far end, in the menu only. */
    secondaryLabel?: string;
    disabled?: boolean;
}

export interface MUISelectProps extends Omit<SelectProps, keyof any> {
    name: string;
    options: readonly Option[];
    formControlProps?: FormControlProps;
    sx?: SxProps;
    color?: 'primary' | 'secondary';
    variant?: 'standard' | 'outlined' | 'filled';
    className?: string;
    fullWidth?: boolean;
    label: ReactNode;
    helperText?: string;
    placeholder?: string;
    disabled?: boolean;
    /**
     * Shows the value without offering a change: the menu does not open and the arrow is dropped, but
     * the field keeps full-contrast text and stays in the tab order. `disabled` greys the value out and
     * skips it, which suits a choice that is unavailable rather than one already made.
     */
    readOnly?: boolean;
    /**
     * `floating` (default) is the house select: the label sits inside the control and rises on
     * selection. `external` draws it statically above, for a form that labels every field the same
     * way down a column.
     */
    labelPosition?: 'floating' | 'external';
    /** How the chosen value reads in the closed control, when that differs from its menu row. */
    renderValue?: SelectProps['renderValue'];
    /** A menu row's content in place of its plain `label`, e.g. a tag. */
    renderOption?: (option: Option) => ReactNode;
}

const NoIcon = () => null;

const Select = ({
    name,
    options = [],
    variant = 'standard',
    className,
    fullWidth,
    formControlProps = {},
    sx = {},
    helperText,
    labelPosition = 'floating',
    renderOption = undefined,
    readOnly = false,
    ...props
}: MUISelectProps) => {
    const [field, { error, touched }] = useField(name);
    const isError = touched && !!error;
    // Whichever text is showing under the field, the field names it as its description.
    const isHelperShown = Boolean(helperText) && !error;
    const helperId = isError || isHelperShown ? `${name}-helper` : undefined;

    return (
        <FormControl
            error={isError}
            fullWidth={fullWidth}
            variant={variant}
            className={className}
            sx={sx}
            {...formControlProps}>
            {props.label && labelPosition === 'external' && (
                /*
                 * Spaced like the theme spaces a TextField's label from its box (`MuiInputLabel`), so
                 * the two line up side by side. Without it MUI's own `label + &` 16px applies, and
                 * the select's line runs 1px short of an input's.
                 */
                <FormLabel
                    id={`${name}-label`}
                    error={isError}
                    sx={{
                        lineHeight: 1.3,
                        '& + .MuiInput-root': { marginTop: '8px' },
                        '& + .MuiInput-root .MuiSelect-select': { minHeight: '1.5em' },
                    }}>
                    {props.label}
                </FormLabel>
            )}
            {props.label && labelPosition === 'floating' && (
                /*
                 * A placeholder switches on `displayEmpty` below, which paints the placeholder text
                 * inside the input while the value is empty — and MUI opens the outlined notch for
                 * it. An unshrunk label lands in that same spot, so the label and the placeholder
                 * were drawn over each other. Shrink whenever the input already has something in it
                 * to read, rather than only when a value is selected.
                 */
                <InputLabel id={`${name}-label`} shrink={!!field.value || !!props.placeholder}>
                    {props.label}
                </InputLabel>
            )}
            <MUISelect
                {...field}
                labelId={`${name}-label`}
                id={`${name}-select`}
                error={isError}
                variant={variant}
                readOnly={readOnly}
                aria-describedby={helperId}
                {...(readOnly && { IconComponent: NoIcon })}
                displayEmpty={!!props.placeholder}
                renderValue={(selected) => {
                    if (selected?.length === 0) {
                        return <Typography color="grey.300">{props.placeholder}</Typography>;
                    }
                    return options.find((opt) => opt.value === selected)?.label;
                }}
                {...props}>
                {props.placeholder && (
                    <MenuItem value="" disabled>
                        {props.placeholder}
                    </MenuItem>
                )}
                {options.map((option) => (
                    <MenuItem
                        key={String(option.value)}
                        /*
                         * MUI types `value` off `<li>`, which has no boolean. `Select` compares
                         * `child.props.value` by identity before the DOM ever sees it, so a boolean
                         * option selects correctly at runtime — `DocumentForm` and
                         * `DocumentRequestFields` both rely on that. Cast here rather than narrowing
                         * `Option.value`, which would break those two callers.
                         */
                        value={option.value as string | number}
                        disabled={option.disabled}
                        sx={option.secondaryLabel ? { justifyContent: 'space-between', gap: 2 } : undefined}>
                        {renderOption ? renderOption(option) : option.label}
                        {option.secondaryLabel && (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                data-option-secondary
                                sx={{ flexShrink: 0 }}>
                                {option.secondaryLabel}
                            </Typography>
                        )}
                    </MenuItem>
                ))}
            </MUISelect>
            {isError && <FormHelperText id={helperId}>{error}</FormHelperText>}
            {isHelperShown && <FormHelperText id={helperId}>{helperText}</FormHelperText>}
        </FormControl>
    );
};

export default Select;
